import React, { useMemo } from 'react';
import { Table, Tag, Button, Space, Typography } from 'antd';
import styled from 'styled-components';
import { SearchInput } from '../../../components/ui/TextInput/SearchInput.styled';
import { PostingUser } from '../../../graphql/query/locationHealthSummaries';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { match } from 'ts-pattern';
import { HealthState } from '../../../graphql/query/getMarketplaceMetrics';
import { DefaultSpinner } from '../../../components/ui/DashboardContent/components/MarketplaceLeaderboard';

const { Column } = Table;
const { Text } = Typography;

const PageContent = styled.div`
  padding: 10px 10px;
  width: 100%;
  align-items: flex-start;
`;

const SearchWrapper = styled.div`
  width: 100%;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

//@ts-ignore
const StyledTag = styled(Tag)`
  font-weight: 500;
  min-width: 100px;
  text-align: center;
`;

//@ts-ignore
const CopyButton = styled(Button)`
  color: #1890ff;
  padding: 0;
  height: auto;
`;

interface PostingUsersTabProps {
  loading: boolean;
  postingUsers: PostingUser[];
  onSearch: (value: string) => void;
  searchText: string;
  showRedUsers?: boolean;
}

export const PostingUsersTab: React.FC<PostingUsersTabProps> = ({
  loading,
  postingUsers,
  onSearch,
  searchText,
  showRedUsers,
}) => {
  const copyToClipboard = (text: string, messageText: string) => {
    navigator.clipboard.writeText(text);
    message.success(messageText);
  };

  const getHealthInfo = (health: string) => {
    return match(health as HealthState)
      .with(HealthState.UnableToPost, () => ({ color: 'red', text: 'Unable to Post' }))
      .with(HealthState.AbleToPost, () => ({ color: 'gold', text: 'Able to Post' }))
      .with(HealthState.Great, () => ({ color: 'green', text: 'Great' }))
      .otherwise(() => ({ color: 'default', text: 'Unknown' }));
  };

  const postingUsersSorted = useMemo(() => {
    const allUsers = [...postingUsers].sort((a, b) => {
      const healthA = a.healthStatus?.health || HealthState.Unknown;
      const healthB = b.healthStatus?.health || HealthState.Unknown;
      const healthOrder = {
        [HealthState.UnableToPost]: 0,
        [HealthState.Unknown]: 1,
        [HealthState.AbleToPost]: 2,
        [HealthState.Great]: 3,
      };
      return (
        (healthOrder[healthA as keyof typeof healthOrder] ?? healthOrder[HealthState.Unknown]) -
        (healthOrder[healthB as keyof typeof healthOrder] ?? healthOrder[HealthState.Unknown])
      );
    });

    let filteredUsers = allUsers;

    if (showRedUsers) {
      filteredUsers = filteredUsers.filter((user) => user.healthStatus?.health === HealthState.UnableToPost);
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredUsers = filteredUsers.filter(
        (user) =>
          user.name.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.token.toLowerCase().includes(searchLower)
      );
    }

    return filteredUsers;
  }, [postingUsers, searchText, showRedUsers]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch(e.target.value);
  };

  const handleExport = () => {
    try {
      const headers = ['Name', 'Token', 'Email', 'Phone', 'Health Status', 'Health Reasons'].join(',');

      const escapeCsvValue = (value: string | undefined | null) => {
        if (value === undefined || value === null) return '""';
        // Escape any existing quotes by doubling them
        const escapedValue = value.replace(/"/g, '""');
        // Wrap in quotes to handle commas and newlines
        return `"${escapedValue}"`;
      };

      const rows = postingUsersSorted
        .map((user) =>
          [
            escapeCsvValue(user.name),
            escapeCsvValue(user.token),
            escapeCsvValue(user.email),
            escapeCsvValue(user.phone),
            escapeCsvValue(user.healthStatus?.health),
            escapeCsvValue(user.healthStatus?.reasons?.join('; ')),
          ].join(',')
        )
        .join('\n');

      const csvContent = `${headers}\n${rows}`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `posting-users-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success('CSV file downloaded successfully');
    } catch (error) {
      message.error('Failed to export CSV file');
      console.error('Export error:', error);
    }
  };

  return (
    <SearchWrapper>
      <SearchRow>
        <SearchInput placeholder="Search posting users" onChange={handleSearch} removeBorder />
        <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
          Export CSV
        </Button>
      </SearchRow>
      <PageContent>
        {loading ? (
          <DefaultSpinner />
        ) : (
          <Table
            pagination={{ pageSize: 50 }}
            className="wide-table"
            dataSource={postingUsersSorted}
            rowKey="id"
            rowClassName={(record) => {
              const health = record.healthStatus?.health?.toLowerCase() || '';
              return `health-row-${health.replace(/\s+/g, '-')}`;
            }}
          >
            <Column<PostingUser>
              title="Name"
              dataIndex="name"
              key="name"
              width="200px"
              render={(name, record) => (
                <UserInfo>
                  <Text strong>{name}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {record.locationId}
                  </Text>
                </UserInfo>
              )}
            />
            <Column<PostingUser>
              title="Token"
              dataIndex="token"
              key="token"
              width="200px"
              render={(token) => (
                <Space>
                  <Text code>{token}</Text>
                  <CopyButton
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(token, 'Token copied to clipboard')}
                  />
                </Space>
              )}
            />
            <Column<PostingUser>
              title="Email"
              dataIndex="email"
              key="email"
              width="200px"
              render={(email) =>
                email ? (
                  <Space>
                    <Text>{email}</Text>
                    <CopyButton
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(email, 'Email copied to clipboard')}
                    />
                  </Space>
                ) : (
                  <Text type="secondary">No email</Text>
                )
              }
            />
            <Column<PostingUser>
              title="Phone"
              dataIndex="phone"
              key="phone"
              width="200px"
              render={(phone) =>
                phone ? (
                  <Space>
                    <Text>{phone}</Text>
                    <CopyButton
                      type="text"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(phone, 'Phone number copied to clipboard')}
                    />
                  </Space>
                ) : (
                  <Text type="secondary">No phone</Text>
                )
              }
            />
            <Column<PostingUser>
              title="Health Status"
              dataIndex="healthStatus"
              key="healthStatus"
              width="200px"
              render={(healthStatus) => (
                <StyledTag color={getHealthInfo(healthStatus?.health || '').color}>
                  {getHealthInfo(healthStatus?.health || '').text}
                </StyledTag>
              )}
            />
            <Column<PostingUser>
              title="Health Reasons"
              dataIndex="healthStatus"
              key="healthReasons"
              render={(healthStatus) => (
                <Space wrap>
                  {healthStatus?.reasons?.map((reason: string, index: number) => (
                    <Tag key={index} color={getHealthInfo(healthStatus?.health || '').color}>
                      {reason}
                    </Tag>
                  ))}
                </Space>
              )}
            />
          </Table>
        )}
      </PageContent>
    </SearchWrapper>
  );
};
